// ink-component
import React from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { ThemeProvider } from 'styled-components';

import { StyledOrderableRow } from './StyledOrderableRow';
import { TwiddleRowContext } from '../NewTableTwiddle/contexts';

export interface NewTableOrderableRowProps extends React.TableHTMLAttributes<HTMLTableRowElement> {
  /** A unique, unchanging key from your data set. Cannot be an index. */
  key: string;
  /** Marks the row as selected. */
  selected?: boolean;
  /** Set background color of all cells in the row. Is overwritten by bgColor of Cell */
  bgColor?: string;
  /** Apply indentation style properties */
  indent?: number;
  /** Apply special style properties for certain child types inside the cell */
  preset?: 'totals';
  /** @ignore */
  rowIndex?: number;
  /** @ignore */
  uniqueKey?: string;
}

/**
 * @group NewTable
 * @purpose Reorder table rows via drag-and-drop or keyboard
 * @categories table
 * @HTMLType HTMLTableRowElement
 */
const NewTableOrderableRow = (props: NewTableOrderableRowProps) => {
  const { level } = React.useContext(TwiddleRowContext);
  const { className, children, bgColor, selected, indent, preset, rowIndex, uniqueKey, ...otherProps } = props;
  const rowRef = React.useRef(null);

  const handleKeyPress = e => {
    if (!otherProps.onClick) return;
    if ((e.key === ' ' || e.key === 'Enter') && e.target === rowRef.current) {
      e.preventDefault();
      otherProps.onClick(e);
    }
  };

  const clickableProps = otherProps.onClick && {
    tabIndex: 0,
    role: 'button',
    onKeyPress: handleKeyPress,
  };

  return (
    <Draggable key={uniqueKey} draggableId={uniqueKey} index={rowIndex}>
      {(provided, snapshot) => (
        <ThemeProvider
          theme={tableTheme => ({
            ...tableTheme,
            rowBgColor: selected ? 'blue-lightest' : bgColor,
            level: indent || level,
            preset,
            rowHasClick: !!otherProps.onClick,
            isDragging: snapshot.isDragging,
          })}
        >
          <StyledOrderableRow
            {...clickableProps}
            {...otherProps}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            ref={provided.innerRef || rowRef}
          >
            {children}
          </StyledOrderableRow>
        </ThemeProvider>
      )}
    </Draggable>
  );
};

NewTableOrderableRow.defaultProps = {
  selected: false,
  bgColor: 'white',
  indent: undefined,
  preset: undefined,
  rowKey: undefined,
};

NewTableOrderableRow.displayName = 'NewTable.OrderableRow';

export default NewTableOrderableRow;
